import { useEffect, useState } from "react";
import { masterApi, apiError, PRODUTOS_LABEL } from "@/lib/api";
import Loader, { useMinLoading } from "@/components/Loader";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Save, Car, MapPin } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterAppDriver() {
  const { unitId, unidades, setUnitId } = useUnit();
  const [cfg, setCfg] = useState(null);
  const [precos, setPrecos] = useState([]);
  const [saving, setSaving] = useState(false);

  // Quando "all", força escolha explícita — evita confusão de editar a unit errada.
  const activeUnit = unitId === "all" ? null : unitId;

  const load = async () => {
    if (!activeUnit) { setCfg(null); setPrecos([]); return; }
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        masterApi.get(`/app_driver/descontos/config?unit_id=${activeUnit}`),
        masterApi.get(`/app_driver/precos?unit_id=${activeUnit}`),
      ]);
      setCfg(c); setPrecos(p);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [activeUnit]);
  const showLoader = useMinLoading(!cfg);

  if (!activeUnit) {
    return (
      <div className="px-4 md:px-6 pt-6 pb-8 space-y-4" data-testid="master-app-driver-no-unit">
        <div className="card-soft rounded-2xl p-6 max-w-2xl space-y-3"
             style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFF1E0 100%)", borderLeft: "4px solid #D8A24E" }}>
          <h1 className="font-serif-display text-2xl text-amber-900">Selecione uma unidade</h1>
          <p className="text-[13px] text-amber-900">
            Preços e descontos da categoria <b>Motorista de App</b> são salvos <b>por unidade</b>.
            Escolha qual deseja configurar:
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {unidades.map(u => (
              <button key={u.id} data-testid={`apd-select-unit-${u.id}`}
                      onClick={() => { setUnitId(u.id); }}
                      className="btn-primary-blue h-10 px-4 text-sm font-medium">
                {u.nome}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (showLoader) return <Loader />;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    const invalido = precos.find(p => !(p.preco_bomba > 0) || !(p.preco_cadastro > 0));
    if (invalido) {
      return modal.error(`Preços App Driver "${PRODUTOS_LABEL[invalido.produto]}" inválidos. Preço Bomba e Preço Final devem ser > 0.`);
    }
    const finalMaior = precos.find(p => Number(p.preco_cadastro) > Number(p.preco_bomba));
    if (finalMaior) {
      return modal.error(`"${PRODUTOS_LABEL[finalMaior.produto]}": o Preço Final p/ App Driver não pode ser maior que o Preço Bomba.`);
    }
    setSaving(true);
    try {
      await masterApi.put("/app_driver/descontos/config", { ...cfg, unit_id: activeUnit });
      for (const p of precos) {
        await masterApi.put("/app_driver/precos", { ...p, unit_id: activeUnit });
      }
      modal.success("Configurações do Motorista de App salvas");
      load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const setPrecoField = (prod, field, value) => {
    setPrecos(precos.map(p => p.produto === prod ? { ...p, [field]: Number(value) } : p));
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-app-driver">
      <div className="card-soft rounded-2xl p-4 border-l-4 border-amber-300 flex items-start gap-3"
           style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFF1E0 100%)" }}>
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
          <Car size={18} className="text-amber-700" />
        </div>
        <div className="flex-1">
          <div className="chip text-[10px] py-0.5 mb-1" style={{ background: "rgba(217,119,6,0.18)", color: "#92400E" }}>Categoria especial</div>
          <div className="font-serif-display text-xl leading-tight">Motorista de App</div>
          <div className="text-[12px] text-amber-900 mt-0.5">
            Preços e descontos <b>isolados</b> da categoria Normal. Estas regras valem apenas para clientes marcados como <b>Motorista de App</b>.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5" style={{ background: "rgba(180,83,9,0.18)", color: "#92400E" }}>
            <Car size={11} /> Motorista de App · <MapPin size={11} /> Editando: <b>{unidades.find(u => u.id === activeUnit)?.nome || ""}</b>
          </div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Descontos & Preços · App Driver</h1>
        </div>
        <button data-testid="save-app-driver-btn" onClick={save} disabled={saving}
                className="btn-action btn-salvar">
          <Save size={15} /> {saving ? "Salvando..." : "Salvar tudo"}
        </button>
      </div>

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2">Preços dos Combustíveis · App Driver</h2>
        <div className="text-[12px] text-neutral-600 mb-3 space-y-0.5">
          <p>O <b>Preço Bomba</b> é o que o frentista digita na bomba (preço real do posto).</p>
          <p>O <b>Preço Final</b> é o que o motorista de app paga, já com o desconto fixo da categoria embutido. A diferença entre os dois é aplicada automaticamente como desconto a cada abastecimento.</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Produto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Preço Bomba</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Preço Final p/ App Driver</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Desconto Fixo (R$/L)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {precos.map((p) => {
                const fixo = (Number(p.preco_bomba) || 0) - (Number(p.preco_cadastro) || 0);
                return (
                <TableRow key={p.produto}>
                  <TableCell className="font-medium">{PRODUTOS_LABEL[p.produto]}</TableCell>
                  <TableCell>
                    <input data-testid={`apd-bomba-${p.produto}`} type="number" step="0.001" value={p.preco_bomba}
                           onChange={(e) => setPrecoField(p.produto, "preco_bomba", e.target.value)}
                           className={inputCls + " w-32"} />
                  </TableCell>
                  <TableCell>
                    <input data-testid={`apd-cad-${p.produto}`} type="number" step="0.001" value={p.preco_cadastro}
                           onChange={(e) => setPrecoField(p.produto, "preco_cadastro", e.target.value)}
                           className={inputCls + " w-32"} />
                  </TableCell>
                  <TableCell>
                    <span data-testid={`apd-fixo-${p.produto}`} className={`text-sm font-semibold ${fixo < 0 ? "text-rose-600" : fixo > 0 ? "text-emerald-700" : "text-neutral-400"}`}>
                      {fixo > 0 ? `− R$ ${fixo.toFixed(3)}/L` : fixo < 0 ? `inválido (Final > Bomba)` : "—"}
                    </span>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
        <p className="text-[10px] text-neutral-500 mt-2">Outros descontos (Fidelidade, Promo do Dia, etc.) cumulam sobre o <b>Preço Bomba</b> junto com o desconto fixo da categoria.</p>
      </section>

      <div className="grid md:grid-cols-2 gap-3">
        <DescSection title="Prêmio de Fidelidade" ativo={cfg.fidelidade_ativo} onToggle={(v) => set("fidelidade_ativo", v)} testid="fidelidade">
          <NumField label="Meta (litros)" value={cfg.fidelidade_litros} onChange={(v) => set("fidelidade_litros", v)} testid="fid-litros" />
          <NumField label="Desconto (¢/L)" value={cfg.fidelidade_desconto_centavos} onChange={(v) => set("fidelidade_desconto_centavos", v)} testid="fid-desc" />
        </DescSection>

        <DescSection title="Cliente Novo" ativo={cfg.cliente_novo_ativo} onToggle={(v) => set("cliente_novo_ativo", v)} testid="clinovo">
          <NumField label="Desconto (¢/L)" value={cfg.cliente_novo_desconto_centavos} onChange={(v) => set("cliente_novo_desconto_centavos", v)} testid="cn-desc" />
          <NumField label="Qtd Abastecimentos" value={cfg.cliente_novo_validade_abastecimentos} onChange={(v) => set("cliente_novo_validade_abastecimentos", parseInt(v))} testid="cn-qtd" />
        </DescSection>

        <DescSection title="Cliente Sumido" ativo={cfg.sumido_ativo} onToggle={(v) => set("sumido_ativo", v)} testid="sumido">
          <NumField label="Dias sem abastecer" value={cfg.sumido_dias} onChange={(v) => set("sumido_dias", parseInt(v))} testid="sum-dias" />
          <NumField label="Desconto (¢/L)" value={cfg.sumido_desconto_centavos} onChange={(v) => set("sumido_desconto_centavos", v)} testid="sum-desc" />
        </DescSection>

        <DescSection title="Cliente Frequente" ativo={cfg.frequencia_ativo} onToggle={(v) => set("frequencia_ativo", v)} testid="freq">
          <NumField label="Mín. abastecimentos/mês" value={cfg.frequencia_min_abastecimentos} onChange={(v) => set("frequencia_min_abastecimentos", parseInt(v))} testid="freq-min" />
          <NumField label="Desconto (¢/L)" value={cfg.frequencia_desconto_centavos} onChange={(v) => set("frequencia_desconto_centavos", v)} testid="freq-desc" />
        </DescSection>

        <DescSection title="Promoção do Dia" ativo={cfg.promo_dia_ativo} onToggle={(v) => set("promo_dia_ativo", v)} testid="promodia">
          <NumField label="Desconto (¢/L)" value={cfg.promo_dia_desconto_centavos} onChange={(v) => set("promo_dia_desconto_centavos", v)} testid="pd-desc" />
        </DescSection>

        <DescSection title="Cashback" ativo={cfg.cashback_ativo} onToggle={(v) => set("cashback_ativo", v)} testid="cashback">
          <NumField label="Centavos por litro (¢/L)" value={cfg.cashback_centavos_litro} onChange={(v) => set("cashback_centavos_litro", v)} testid="cb-cents" />
          <NumField label="Dias de validade" value={cfg.cashback_dias_validade} onChange={(v) => set("cashback_dias_validade", parseInt(v))} testid="cb-dias" />
        </DescSection>

        <DescSection title="Oferta com Prazo" ativo={cfg.gamificacao_ativo} onToggle={(v) => set("gamificacao_ativo", v)} testid="gam">
          <NumField label="Desconto (¢/L)" value={cfg.gamificacao_desconto_centavos} onChange={(v) => set("gamificacao_desconto_centavos", v)} testid="gm-desc" />
          <Field label="Data limite">
            <input type="date" data-testid="gm-data" value={(cfg.gamificacao_data_limite || "").slice(0, 10)}
                   onChange={(e) => set("gamificacao_data_limite", e.target.value)} className={inputCls} />
          </Field>
        </DescSection>

        <DescSection title="Abastecimentos Alternados" ativo={cfg.alternados_ativo} onToggle={(v) => set("alternados_ativo", v)} testid="alt">
          <NumField label="Desconto (¢/L)" value={cfg.alternados_desconto_centavos} onChange={(v) => set("alternados_desconto_centavos", v)} testid="alt-desc" />
        </DescSection>

        <DescSection title="Troca de Óleo" ativo={cfg.oleo_ativo} onToggle={(v) => set("oleo_ativo", v)} testid="oleo">
          <NumField label="Meta (litros)" value={cfg.oleo_litros_meta} onChange={(v) => set("oleo_litros_meta", v)} testid="ol-meta" />
          <NumField label="Percentual (%)" value={cfg.oleo_percentual} onChange={(v) => set("oleo_percentual", v)} testid="ol-pct" />
          <NumField label="Validade do prêmio (dias)" value={cfg.oleo_validade_dias ?? 30} onChange={(v) => set("oleo_validade_dias", v)} testid="ol-validade" />
        </DescSection>

        <DescSection title="Mensagem Recompensa" ativo={cfg.recompensa_ativo} onToggle={(v) => set("recompensa_ativo", v)} testid="recomp">
          <NumField label="Litros acumulados" value={cfg.recompensa_litros} onChange={(v) => set("recompensa_litros", v)} testid="rc-litros" />
          <Field label="Texto">
            <textarea data-testid="rc-texto" value={cfg.recompensa_texto} onChange={(e) => set("recompensa_texto", e.target.value)}
                      rows={2}
                      className="w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
          </Field>
        </DescSection>

        <DescSection title="Oferta Aditivada" ativo={cfg.aditivada_ativo} onToggle={(v) => set("aditivada_ativo", v)} testid="adit">
          <NumField label="Desconto por litro (¢/L)" value={cfg.aditivada_desconto_centavos} onChange={(v) => set("aditivada_desconto_centavos", v)} testid="adit-desc" />
        </DescSection>

        <DescSection title="Indicação" ativo={cfg.indicacao_ativo} onToggle={(v) => set("indicacao_ativo", v)} testid="ind">
          <NumField label="Prêmio (R$)" value={cfg.indicacao_premio_reais} onChange={(v) => set("indicacao_premio_reais", v)} testid="ind-premio" />
          <NumField label="Validade (dias)" value={cfg.indicacao_validade_dias} onChange={(v) => set("indicacao_validade_dias", parseInt(v))} testid="ind-dias" />
        </DescSection>

        <DescSection title="Cupom Saudade" ativo={true} hideToggle testid="saud">
          <NumField label="Desconto (¢/L)" value={cfg.saudade_desconto_centavos} onChange={(v) => set("saudade_desconto_centavos", v)} testid="sd-desc" />
        </DescSection>
      </div>

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2">Mensagem do Cupom (Pós-Atendimento)</h2>
        <textarea data-testid="apd-mensagem-cupom" value={cfg.mensagem_cupom || ""} onChange={(e) => set("mensagem_cupom", e.target.value)}
                  rows={3} placeholder="Deixe vazio para pular esta tela."
                  className="w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
      </section>
    </div>
  );
}

function DescSection({ title, ativo, onToggle, hideToggle, children, testid }) {
  return (
    <section className="card-soft rounded-xl p-3.5 space-y-2.5" data-testid={`apd-section-${testid}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-base leading-tight">{title}</h3>
        {!hideToggle && <Switch data-testid={`apd-toggle-${testid}`} checked={!!ativo} onCheckedChange={onToggle} />}
      </div>
      <div className="space-y-2" style={{ opacity: ativo || hideToggle ? 1 : 0.45 }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  const inputCls2 = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";
  return (
    <Field label={label}>
      <input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             data-testid={`apd-numfield-${testid}`} className={inputCls2} />
    </Field>
  );
}
